<script setup lang="ts">
import { MeasureResult } from '@Shared/utils/types';
import { calculateDeltaValue, calculateMetricValue, DeltaType, MetricType, formatDeltaValue } from './utility';
import { roundAwayFromZero } from '@Shared/utils/util';

import DownArrowSvg from './visualAssets/DownArrow.svg?component';
import UpArrowSvg from './visualAssets/UpArrow.svg?component';
import LevelArrowSvg from './visualAssets/LevelArrow.svg?component';
import { useScreen } from '@Shared/utils/useScreen';

const props = defineProps<{
  title: string;
  measureResults: MeasureResult[];
  metricType: MetricType;
}>();

const getColorForDeltaValue = (driver: MeasureResult): string => {
  const deltaValue = calculateDeltaValue(driver, DeltaType.PointDelta);

  if (deltaValue == null) return '#7F7466';
  if (deltaValue < 0) return '#D4002B';
  if (deltaValue == 0) return '#7F7466';
  if (deltaValue > 0) return '#0C8A65';

  return '';
};

const screenSize = useScreen();

const getWidthForDeltaValue = (driver: MeasureResult): string => {
  const deltaValue = calculateDeltaValue(driver, DeltaType.PointDelta);

  let scale = 0.75;
  if (screenSize.value.width >= 3840) {
    scale = 2;
  } else if (screenSize.value.width >= 1920) {
    scale = 1;
  }

  if (deltaValue == null || deltaValue == 0) return `${25 * scale}px`;
  return `${20 * scale}px`;
};

const getMaxWidthForDeltaValue = (): string => {
  let scale = 0.75;
  if (screenSize.value.width >= 3840) {
    scale = 2;
  } else if (screenSize.value.width >= 1920) {
    scale = 1;
  }

  return `${30 * scale}px`;
};

const calculateAndFormatDeltaValue = (driver: MeasureResult): string => {
  const deltaValue = calculateDeltaValue(driver, DeltaType.PointDelta);
  if (props.metricType === MetricType.RawScoreAverage) return formatDeltaValue(deltaValue, DeltaType.RawDelta);
  return formatDeltaValue(deltaValue, DeltaType.PointDelta);
};

const deltaValueImg = (driver: MeasureResult) => {
  const deltaValue = calculateDeltaValue(driver, DeltaType.PointDelta);
  if (deltaValue == null) return null;

  if (roundAwayFromZero(deltaValue * 100) < 0) {
    return DownArrowSvg;
  } else if (roundAwayFromZero(deltaValue * 100) > 0) {
    return UpArrowSvg;
  } else {
    return LevelArrowSvg;
  }
};

const results = computed(() =>
  [...props.measureResults]
    .filter((a) => a.currentValue != null)
    // validation above, too messy to avoid
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    .sort((a, b) => b.currentValue! - a.currentValue!)
    .slice(0, 5)
);
</script>
<template>
  <div class="h-full flex flex-col">
    <div
      class="bg-[#CCD1D8] border-solid border-2 2xl:border-4 3xl:border-8 border-white rounded-t-2 pt-1 pl-4 pb-2 2xl:pt-4 2xl:pb-5 3xl:pt-9 3xl:pb-10 3xl:pl-6 uppercase font-bold xl:text-4 2xl:text-5 3xl:text-10 text-lg text-[#4F4F4F] font-family[Poppins]"
      id="MeasureLeaderboard"
    >
      {{ title }}
    </div>
    <div class="rounded-b-4 bg-white pr-4 pl-4 pb-1 2xl:pb-2 3xl:pb-4 3xl:pl-6 flex-grow">
      <div
        :style="{
          background: index % 2 != 0 ? '#CCD1D8' : undefined,
          'grid-template-columns': `66% 12% ${getMaxWidthForDeltaValue()} auto`,
        }"
        v-for="(item, index) in results"
        :key="item.label"
        class="grid rounded text-[#333333] pl-2 pb-1 pt-1 pr-2 3xl:pt-4 3xl:pb-4 xl:text-4 2xl:text-7 3xl:text-12 flex-grow"
      >
        <div class="truncate">
          {{ item.label }}
        </div>
        <div class="text-center font-semibold">
          {{ calculateMetricValue(item.currentValue, metricType) }}
        </div>
        <div class="text-right">
          <component :width="getWidthForDeltaValue(item)" :is="deltaValueImg(item)" />
        </div>
        <div
          class="text-left ml-2 font-semibold"
          :style="{
            color: getColorForDeltaValue(item),
          }"
        >
          {{ calculateAndFormatDeltaValue(item) }}
        </div>
      </div>
    </div>
  </div>
</template>
